import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "../../api/client";
import { PageContainer } from "../../components/layout/PageContainer";
import { Card, CardBody, CardHeader, CardTitle } from "../../ui/Card";

type CompanyDetail = {
  companyId: string;
  companyLegalName: string;
  backOfficeId: string;
  status: "Active" | "Inactive";
  onboardedBy: string;
  onboardedAt: string;
  taxId: string;
  legalAddress: string;
  sourceBankCode: string;
  sourceBankAccount: string;
  perBatchLimitIdr: number;
  perTransactionFeeIdr: number;
  picFullname: string;
  picPhonenumber: string;
  picUsername: string;
  reasonDescription: string;
  totalRecipient: number;
  totalTransactions: number;
  totalTransactionsAmountIdr: number;
  totalFeesIdr: number;
};

const COMPANY_DETAIL_KEY = (id: string) => ["company-detail", id];

function fetchCompanyDetail(id: string): Promise<CompanyDetail> {
  return apiRequest<CompanyDetail>({
    path: `/company/${id}`,
    method: "GET",
    useMock: true,
    mockResponse: {
      companyId: id,
      companyLegalName: "KamuKirim Demo Co",
      backOfficeId: "IMMER-01",
      status: "Active",
      onboardedBy: "admin@kamukirim.mock",
      onboardedAt: new Date().toISOString(),
      taxId: "01.234.567.8-901.000",
      legalAddress: "Jl. Sudirman No. 1, Jakarta",
      sourceBankCode: "002",
      sourceBankAccount: "1234567890",
      perBatchLimitIdr: 100_000_000,
      perTransactionFeeIdr: 2_500,
      picFullname: "Rama Wirawan",
      picPhonenumber: "+62 812 0000 1234",
      picUsername: "immer.ext-auditor",
      reasonDescription: "Initial onboarding for payroll rollout.",
      totalRecipient: 2560,
      totalTransactions: 128,
      totalTransactionsAmountIdr: 9_850_000_000,
      totalFeesIdr: 320_000_000,
    },
    delayMs: 260,
  });
}

function formatIdr(value: number): string {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDateTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function CompanyDetailPage() {
  const params = useParams();
  const companyId = params.companyId ?? "";

  const { data, isError, error, isLoading } = useQuery({
    queryKey: COMPANY_DETAIL_KEY(companyId),
    queryFn: () => fetchCompanyDetail(companyId),
    enabled: Boolean(companyId),
  });

  const detail = data;

  return (
    <PageContainer
      title="Company detail"
      description={companyId ? `ID: ${companyId}` : "No company ID provided."}
      width="wide"
      padding="lg"
    >
      {isError ? (
        <Card>
          <CardBody>
            <div className="text-sm text-[color:var(--kkp-text-muted)]">
              Failed to load company detail.{" "}
              {String((error as Error)?.message ?? "")}
            </div>
          </CardBody>
        </Card>
      ) : null}

      {isLoading ? (
        <Card>
          <CardBody>
            <div className="text-sm text-[color:var(--kkp-text-muted)]">
              Loading…
            </div>
          </CardBody>
        </Card>
      ) : null}

      {detail ? (
        <Card>
          <CardHeader>
            <CardTitle as="h2">{detail.companyLegalName}</CardTitle>
          </CardHeader>
          <CardBody className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            <Field label="Company legal name" value={detail.companyLegalName} />
            <Field label="Back-office ID" value={detail.backOfficeId} />
            <Field label="Tax ID (NPWP)" value={detail.taxId} />
            <Field label="Legal address" value={detail.legalAddress} />
            <Field label="Source bank code" value={detail.sourceBankCode} />
            <Field
              label="Source bank account"
              value={detail.sourceBankAccount}
            />
            <Field
              label="Per batch limit (IDR)"
              value={formatIdr(detail.perBatchLimitIdr)}
            />
            <Field
              label="Per transaction fee (IDR)"
              value={formatIdr(detail.perTransactionFeeIdr)}
            />
            <Field label="PIC fullname" value={detail.picFullname} />
            <Field label="PIC phonenumber" value={detail.picPhonenumber} />
            <Field label="PIC username" value={detail.picUsername} />
            <Field label="Reason/description" value={detail.reasonDescription} />
            <Field label="Onboarded at" value={formatDateTime(detail.onboardedAt)} />
            <Field label="Onboarded by" value={detail.onboardedBy} />
            <Field label="Status" value={detail.status} />
            <Field
              label="Total recipient"
              value={detail.totalRecipient.toLocaleString("id-ID")}
            />
            <Field
              label="Total transactions"
              value={detail.totalTransactions.toLocaleString("id-ID")}
            />
            <Field
              label="Total transactions amount (IDR)"
              value={formatIdr(detail.totalTransactionsAmountIdr)}
            />
            <Field
              label="Total fees (IDR)"
              value={formatIdr(detail.totalFeesIdr)}
            />
          </CardBody>
        </Card>
      ) : null}
    </PageContainer>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] px-3 py-3 shadow-sm">
      <div className="text-[11px] font-semibold text-[color:var(--kkp-text-muted)]">
        {label}
      </div>
      <div className="mt-1 text-sm font-bold text-[color:var(--kkp-text)]">
        {value}
      </div>
    </div>
  );
}
